import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
  limit,
  startAfter,
  DocumentSnapshot,
} from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';

export interface FinanceTransaction {
  id: string;
  date: Date | Timestamp;
  particulars: string;
  ref?: string;
  debit: number;
  credit: number;
  balance: number;
  type: 'expense' | 'invoice' | 'manual' | 'adjustment';
  category?: string;
  description?: string;
  createdBy: string;
  createdAt: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

export interface Expense {
  id: string;
  date: Date | Timestamp;
  amount: number;
  category: string;
  description: string;
  vendor?: string;
  paymentMethod?: string;
  ref?: string;
  status: 'paid' | 'pending';
  reverted?: boolean;
  revertReason?: string;
  revertedAt?: Date | Timestamp;
  revertedBy?: string;
  createdBy: string;
  createdAt: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  date: Date | Timestamp;
  amount: number;
  paidAmount?: number; // Amount received/paid for this invoice
  clientName: string;
  clientType?: 'Individual' | 'Factory' | 'Bank';
  description: string;
  status: 'received' | 'pending' | 'overdue';
  dueDate?: Date | Timestamp;
  paymentDate?: Date | Timestamp;
  paymentMethod?: string;
  ref?: string;
  reverted?: boolean;
  revertReason?: string;
  revertedAt?: Date | Timestamp;
  revertedBy?: string;
  createdBy: string;
  createdAt: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

/**
 * Get all transactions for a company with pagination and filtering
 */
export const fetchTransactions = async (
  companyId: string,
  options?: {
    timeFilter?: 'week' | 'month' | 'half-year' | 'year' | 'all';
    type?: 'expense' | 'invoice' | 'manual' | 'adjustment';
    pageSize?: number;
    lastDoc?: DocumentSnapshot;
  }
): Promise<{ transactions: FinanceTransaction[]; lastDoc: DocumentSnapshot | null; hasMore: boolean }> => {
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const transactionsRef = collection(financeDocRef, 'transactions');
  const pageSize = options?.pageSize || 20;
  
  let q = query(transactionsRef, orderBy('createdAt', 'desc'), orderBy('date', 'desc'));
  
  // Add type filter if provided
  if (options?.type) {
    q = query(q, where('type', '==', options.type));
  }
  
  // Add time filter if provided
  if (options?.timeFilter && options.timeFilter !== 'all') {
    const now = new Date();
    let startDate: Date;
    
    switch (options.timeFilter) {
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'half-year':
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        startDate = new Date(0);
    }
    
    q = query(
      q,
      where('date', '>=', Timestamp.fromDate(startDate))
    );
  }
  
  // Add pagination
  q = query(q, limit(pageSize + 1)); // Fetch one extra to check if there's more
  
  if (options?.lastDoc) {
    q = query(q, startAfter(options.lastDoc));
  }
  
  const snapshot = await getDocs(q);
  const docs = snapshot.docs;
  const hasMore = docs.length > pageSize;
  const transactionsDocs = hasMore ? docs.slice(0, pageSize) : docs;
  
  const transactions = transactionsDocs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined,
    } as FinanceTransaction;
  });
  
  return {
    transactions,
    lastDoc: transactionsDocs.length > 0 ? transactionsDocs[transactionsDocs.length - 1] : null,
    hasMore,
  };
};

/**
 * Get transaction summary
 */
export const getTransactionSummary = async (
  companyId: string,
  timeFilter?: 'week' | 'month' | 'half-year' | 'year' | 'all',
  type?: 'expense' | 'invoice' | 'manual' | 'adjustment'
): Promise<{
  totalDebit: number;
  totalCredit: number;
  balance: number;
  transactionCount: number;
}> => {
  // For summary, we need all transactions, so fetch without pagination
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const transactionsRef = collection(financeDocRef, 'transactions');
  let q = query(transactionsRef, orderBy('createdAt', 'desc'), orderBy('date', 'desc'));
  
  if (type) {
    q = query(q, where('type', '==', type));
  }
  
  if (timeFilter && timeFilter !== 'all') {
    const now = new Date();
    let startDate: Date;
    
    switch (timeFilter) {
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'half-year':
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        startDate = new Date(0);
    }
    
    q = query(q, where('date', '>=', Timestamp.fromDate(startDate)));
  }
  
  const snapshot = await getDocs(q);
  const transactions = snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
    } as FinanceTransaction;
  });
  
  const totalDebit = transactions.reduce((sum: number, t: FinanceTransaction) => sum + (t.debit || 0), 0);
  const totalCredit = transactions.reduce((sum: number, t: FinanceTransaction) => sum + (t.credit || 0), 0);
  const balance = totalDebit - totalCredit;
  
  return {
    totalDebit,
    totalCredit,
    balance,
    transactionCount: transactions.length,
  };
};

/**
 * Add a transaction
 */
export const addTransaction = async (
  companyId: string,
  transactionData: Omit<FinanceTransaction, 'id' | 'createdAt' | 'updatedAt' | 'balance' | 'createdBy'>
): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Get last transaction to calculate balance
  // We need to fetch all transactions ordered by createdAt to get the correct last balance
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const transactionsRef = collection(financeDocRef, 'transactions');
  const q = query(transactionsRef, orderBy('createdAt', 'desc'), orderBy('date', 'desc'));
  const snapshot = await getDocs(q);
  
  let lastBalance = 0;
  if (snapshot.docs.length > 0) {
    const lastTransaction = snapshot.docs[0].data();
    lastBalance = lastTransaction.balance || 0;
  }
  
  const newBalance = lastBalance + transactionData.debit - transactionData.credit;

  const transactionRef = doc(transactionsRef);
  
  let transactionDate: Date;
  if (transactionData.date instanceof Date) {
    transactionDate = transactionData.date;
  } else if (transactionData.date && typeof (transactionData.date as any).toDate === 'function') {
    transactionDate = (transactionData.date as any).toDate();
  } else if (transactionData.date && typeof transactionData.date === 'object' && (transactionData.date as any).seconds) {
    const ts = transactionData.date as Timestamp;
    transactionDate = ts.toDate();
  } else {
    transactionDate = new Date(transactionData.date as any);
  }

  const transaction: any = {
    ...transactionData,
    balance: newBalance,
    createdBy: user.uid,
    createdAt: serverTimestamp(),
    date: Timestamp.fromDate(transactionDate),
  };

  await setDoc(transactionRef, transaction);
  
  return transactionRef.id;
};

/**
 * Recalculate balances for all transactions after a deletion
 */
const recalculateBalances = async (companyId: string): Promise<void> => {
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const transactionsRef = collection(financeDocRef, 'transactions');
  const q = query(transactionsRef, orderBy('createdAt', 'asc'), orderBy('date', 'asc'));
  const snapshot = await getDocs(q);
  
  let runningBalance = 0;
  const updatePromises: Promise<void>[] = [];
  
  snapshot.docs.forEach((doc) => {
    const data = doc.data();
    const debit = data.debit || 0;
    const credit = data.credit || 0;
    runningBalance = runningBalance + debit - credit;
    
    // Only update if balance has changed
    if (data.balance !== runningBalance) {
      updatePromises.push(
        updateDoc(doc.ref, {
          balance: runningBalance,
        })
      );
    }
  });
  
  await Promise.all(updatePromises);
};

/**
 * Get all expenses
 */
export const fetchExpenses = async (companyId: string): Promise<Expense[]> => {
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const expensesRef = collection(financeDocRef, 'expenses');
  const q = query(expensesRef, orderBy('date', 'desc'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      status: data.status || 'paid', // Default to 'paid' for backward compatibility
      date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined,
    } as Expense;
  });
};

/**
 * Add an expense
 */
export const addExpense = async (
  companyId: string,
  expenseData: Omit<Expense, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'status'> & { status?: 'paid' | 'pending' }
): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const expensesRef = collection(financeDocRef, 'expenses');
  const expenseRef = doc(expensesRef);
  
  let expenseDate: Date;
  if (expenseData.date instanceof Date) {
    expenseDate = expenseData.date;
  } else if (expenseData.date && typeof (expenseData.date as any).toDate === 'function') {
    expenseDate = (expenseData.date as any).toDate();
  } else if (expenseData.date && typeof expenseData.date === 'object' && (expenseData.date as any).seconds) {
    const ts = expenseData.date as Timestamp;
    expenseDate = ts.toDate();
  } else {
    expenseDate = new Date(expenseData.date as any);
  }

  const expense: any = {
    amount: expenseData.amount,
    category: expenseData.category,
    description: expenseData.description,
    status: expenseData.status || 'paid', // Auto set to 'paid' if not provided
    createdBy: user.uid,
    createdAt: serverTimestamp(),
    date: Timestamp.fromDate(expenseDate),
  };

  // Only add optional fields if they have truthy values (not empty strings or undefined)
  if (expenseData.vendor && expenseData.vendor.trim()) {
    expense.vendor = expenseData.vendor;
  }
  if (expenseData.paymentMethod && expenseData.paymentMethod.trim()) {
    expense.paymentMethod = expenseData.paymentMethod;
  }
  if (expenseData.ref && expenseData.ref.trim()) {
    expense.ref = expenseData.ref;
  }

  await setDoc(expenseRef, expense);
  
  // Also add as transaction (credit - expense)
  await addTransaction(companyId, {
    date: expenseData.date,
    particulars: expenseData.description || `Expense: ${expenseData.category}`,
    ref: (expenseData.ref && expenseData.ref.trim()) ? expenseData.ref : expenseRef.id,
    debit: 0,
    credit: expenseData.amount,
    type: 'expense',
    category: expenseData.category,
    description: expenseData.description,
  });
  
  return expenseRef.id;
};

/**
 * Delete an expense and its corresponding transaction
 */
export const deleteExpense = async (companyId: string, expenseId: string): Promise<void> => {
  // Get the expense to find its transaction
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const expenseRef = doc(collection(financeDocRef, 'expenses'), expenseId);
  const expenseSnap = await getDoc(expenseRef);
  
  if (!expenseSnap.exists()) {
    throw new Error('Expense not found');
  }

  // Find and delete the corresponding transaction
  const transactionsRef = collection(financeDocRef, 'transactions');
  const transactionsQuery = query(
    transactionsRef,
    where('type', '==', 'expense'),
    where('ref', '==', expenseId)
  );
  const transactionsSnapshot = await getDocs(transactionsQuery);
  
  // Delete all matching transactions (should be only one)
  const deletePromises = transactionsSnapshot.docs.map((doc) => deleteDoc(doc.ref));
  await Promise.all(deletePromises);

  // Delete the expense
  await deleteDoc(expenseRef);

  // Recalculate balances for all transactions after the deleted one
  await recalculateBalances(companyId);
};

/**
 * Get all invoices
 */
export const fetchInvoices = async (companyId: string): Promise<Invoice[]> => {
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const invoicesRef = collection(financeDocRef, 'invoices');
  const q = query(invoicesRef, orderBy('date', 'desc'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      reverted: data.reverted || false,
      revertedAt: data.revertedAt?.toDate ? data.revertedAt.toDate() : data.revertedAt ? new Date(data.revertedAt) : undefined,
      date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
      dueDate: data.dueDate?.toDate ? data.dueDate.toDate() : data.dueDate ? new Date(data.dueDate) : undefined,
      paymentDate: data.paymentDate?.toDate ? data.paymentDate.toDate() : data.paymentDate ? new Date(data.paymentDate) : undefined,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined,
    } as Invoice;
  });
};

/**
 * Add an invoice
 */
export const addInvoice = async (
  companyId: string,
  invoiceData: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'invoiceNumber'> & { invoiceNumber?: string }
): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Generate invoice number if not provided
  let invoiceNumber: string;
  if (invoiceData.invoiceNumber && invoiceData.invoiceNumber.trim()) {
    invoiceNumber = invoiceData.invoiceNumber.trim();
  } else {
    const invoices = await fetchInvoices(companyId);
    invoiceNumber = `INV-${String(invoices.length + 1).padStart(6, '0')}`;
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const invoicesRef = collection(financeDocRef, 'invoices');
  const invoiceRef = doc(invoicesRef);
  
  let invoiceDate: Date;
  if (invoiceData.date instanceof Date) {
    invoiceDate = invoiceData.date;
  } else if (invoiceData.date && typeof (invoiceData.date as any).toDate === 'function') {
    invoiceDate = (invoiceData.date as any).toDate();
  } else if (invoiceData.date && typeof invoiceData.date === 'object' && (invoiceData.date as any).seconds) {
    const ts = invoiceData.date as Timestamp;
    invoiceDate = ts.toDate();
  } else {
    invoiceDate = new Date(invoiceData.date as any);
  }
  
  let dueDateObj: Date | undefined;
  if (invoiceData.dueDate) {
    if (invoiceData.dueDate instanceof Date) {
      dueDateObj = invoiceData.dueDate;
    } else if (typeof (invoiceData.dueDate as any).toDate === 'function') {
      dueDateObj = (invoiceData.dueDate as any).toDate();
    } else if (typeof invoiceData.dueDate === 'object' && (invoiceData.dueDate as any).seconds) {
      const ts = invoiceData.dueDate as Timestamp;
      dueDateObj = ts.toDate();
    } else {
      dueDateObj = new Date(invoiceData.dueDate as any);
    }
  }
  
  let paymentDateObj: Date | undefined;
  if (invoiceData.paymentDate) {
    if (invoiceData.paymentDate instanceof Date) {
      paymentDateObj = invoiceData.paymentDate;
    } else if (typeof (invoiceData.paymentDate as any).toDate === 'function') {
      paymentDateObj = (invoiceData.paymentDate as any).toDate();
    } else if (typeof invoiceData.paymentDate === 'object' && (invoiceData.paymentDate as any).seconds) {
      const ts = invoiceData.paymentDate as Timestamp;
      paymentDateObj = ts.toDate();
    } else {
      paymentDateObj = new Date(invoiceData.paymentDate as any);
    }
  }

  const invoice: any = {
    companyId,
    invoiceNumber,
    amount: invoiceData.amount,
    clientName: invoiceData.clientName,
    clientType: invoiceData.clientType,
    description: invoiceData.description,
    status: invoiceData.status,
    createdBy: user.uid,
    createdAt: serverTimestamp(),
    date: Timestamp.fromDate(invoiceDate),
  };

  // Only add optional fields if they are provided
  if (dueDateObj) {
    invoice.dueDate = Timestamp.fromDate(dueDateObj);
  }

  if (paymentDateObj) {
    invoice.paymentDate = Timestamp.fromDate(paymentDateObj);
  }

  if (invoiceData.paymentMethod) {
    invoice.paymentMethod = invoiceData.paymentMethod;
  }

  await setDoc(invoiceRef, invoice);
  
  // If invoice is received, add as transaction (debit - received)
  if (invoiceData.status === 'received') {
    await addTransaction(companyId, {
      date: invoiceData.paymentDate || invoiceData.date,
      particulars: `Invoice ${invoiceNumber} - ${invoiceData.clientName}`,
      ref: invoiceNumber,
      debit: invoiceData.amount,
      credit: 0,
      type: 'invoice',
      description: invoiceData.description,
    });
  }
  
  // Create account receivable if invoice is not fully received
  // This allows tracking of pending/overdue invoices in AccountReceivablePage
  if (invoiceData.status !== 'received') {
    try {
      const { createAccountReceivableFromInvoice } = await import('./accountReceivableService');
      await createAccountReceivableFromInvoice(companyId, {
        id: invoiceRef.id,
        invoiceNumber,
        date: invoiceData.date,
        amount: invoiceData.amount,
        clientName: invoiceData.clientName,
        clientType: invoiceData.clientType,
        description: invoiceData.description,
        dueDate: invoiceData.dueDate,
        status: invoiceData.status,
      });
    } catch (error) {
      console.error('Error creating account receivable from invoice:', error);
      // Don't throw - account receivable creation failure shouldn't break invoice creation
    }
  }
  
  return invoiceRef.id;
};

/**
 * Update invoice status (when received, add transaction)
 */
export const updateInvoiceStatus = async (
  companyId: string,
  invoiceId: string,
  status: 'received' | 'pending' | 'overdue',
  paymentDate?: Date
): Promise<void> => {
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const invoiceRef = doc(collection(financeDocRef, 'invoices'), invoiceId);
  const invoiceSnap = await getDoc(invoiceRef);
  
  if (!invoiceSnap.exists()) {
    throw new Error('Invoice not found');
  }
  
  const invoiceData = invoiceSnap.data() as Invoice;
  
  const updateData: any = {
    status,
    updatedAt: serverTimestamp(),
  };
  
  if (paymentDate) {
    updateData.paymentDate = Timestamp.fromDate(paymentDate);
  }
  
  await updateDoc(invoiceRef, updateData);
  
  // If status changed to received and wasn't received before, add transaction (debit - received)
  if (status === 'received' && invoiceData.status !== 'received') {
    await addTransaction(companyId, {
      date: paymentDate || new Date(),
      particulars: `Invoice ${invoiceData.invoiceNumber} - ${invoiceData.clientName}`,
      ref: invoiceData.invoiceNumber,
      debit: invoiceData.amount,
      credit: 0,
      type: 'invoice',
      description: invoiceData.description,
    });
  }
};

/**
 * Delete an invoice and its corresponding transaction
 */
export const deleteInvoice = async (companyId: string, invoiceId: string): Promise<void> => {
  // Get the invoice to find its invoice number
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const invoiceRef = doc(collection(financeDocRef, 'invoices'), invoiceId);
  const invoiceSnap = await getDoc(invoiceRef);
  
  if (!invoiceSnap.exists()) {
    throw new Error('Invoice not found');
  }

  const invoiceData = invoiceSnap.data() as Invoice;
  const invoiceNumber = invoiceData.invoiceNumber;

  // Find and delete the corresponding transaction
  const transactionsRef = collection(financeDocRef, 'transactions');
  const transactionsQuery = query(
    transactionsRef,
    where('type', '==', 'invoice'),
    where('ref', '==', invoiceNumber)
  );
  const transactionsSnapshot = await getDocs(transactionsQuery);
  
  // Delete all matching transactions (should be only one)
  const deletePromises = transactionsSnapshot.docs.map((doc) => deleteDoc(doc.ref));
  await Promise.all(deletePromises);

  // Delete the invoice
  await deleteDoc(invoiceRef);

  // Recalculate balances for all transactions after the deleted one
  await recalculateBalances(companyId);
};

/**
 * Revert an invoice - creates a credit transaction (negative of original debit)
 */
export const revertInvoice = async (
  companyId: string,
  invoiceId: string,
  reason: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const invoiceRef = doc(collection(financeDocRef, 'invoices'), invoiceId);
  const invoiceSnap = await getDoc(invoiceRef);
  
  if (!invoiceSnap.exists()) {
    throw new Error('Invoice not found');
  }

  const invoiceData = invoiceSnap.data() as Invoice;
  
  if (invoiceData.reverted) {
    throw new Error('Invoice has already been reverted');
  }

  const invoiceNumber = invoiceData.invoiceNumber;

  // Find the original transaction
  const transactionsRef = collection(financeDocRef, 'transactions');
  const transactionsQuery = query(
    transactionsRef,
    where('type', '==', 'invoice'),
    where('ref', '==', invoiceNumber)
  );
  const transactionsSnapshot = await getDocs(transactionsQuery);
  
  if (transactionsSnapshot.empty) {
    throw new Error('Original transaction not found');
  }

  // Create a NEW credit transaction (negative of original debit) to revert
  // This creates a separate entry in the transaction list, not overwriting the original
  await addTransaction(companyId, {
    date: new Date(),
    particulars: `Revert: Invoice ${invoiceNumber} - ${invoiceData.clientName}`,
    ref: `${invoiceNumber}-REVERT`, // Unique ref to distinguish from original
    debit: 0,
    credit: invoiceData.amount, // Credit to reverse the original debit
    type: 'invoice',
    description: `Reverted: ${invoiceData.description}. Reason: ${reason}`,
  });

  // Mark invoice as reverted
  await updateDoc(invoiceRef, {
    reverted: true,
    revertReason: reason,
    revertedAt: serverTimestamp(),
    revertedBy: user.uid,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Revert an expense - creates a debit transaction (negative of original credit)
 */
export const revertExpense = async (
  companyId: string,
  expenseId: string,
  reason: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const expenseRef = doc(collection(financeDocRef, 'expenses'), expenseId);
  const expenseSnap = await getDoc(expenseRef);
  
  if (!expenseSnap.exists()) {
    throw new Error('Expense not found');
  }

  const expenseData = expenseSnap.data() as Expense;
  
  if (expenseData.reverted) {
    throw new Error('Expense has already been reverted');
  }

  // Find the original transaction - it could be using expenseId or expenseData.ref
  const transactionsRef = collection(financeDocRef, 'transactions');
  let transactionsQuery = query(
    transactionsRef,
    where('type', '==', 'expense'),
    where('ref', '==', expenseId)
  );
  let transactionsSnapshot = await getDocs(transactionsQuery);
  
  // If not found, try with expenseData.ref if it exists
  if (transactionsSnapshot.empty && expenseData.ref) {
    transactionsQuery = query(
      transactionsRef,
      where('type', '==', 'expense'),
      where('ref', '==', expenseData.ref)
    );
    transactionsSnapshot = await getDocs(transactionsQuery);
  }
  
  if (transactionsSnapshot.empty) {
    throw new Error('Original transaction not found');
  }

  // Create a NEW debit transaction (negative of original credit) to revert
  // This creates a separate entry in the transaction list, not overwriting the original
  const originalExpenseRef = expenseData.ref && expenseData.ref.trim() ? expenseData.ref : expenseId;
  await addTransaction(companyId, {
    date: new Date(),
    particulars: `Revert: Expense - ${expenseData.category}`,
    ref: `${originalExpenseRef}-REVERT`, // Unique ref to distinguish from original
    debit: expenseData.amount, // Debit to reverse the original credit
    credit: 0,
    type: 'expense',
    category: expenseData.category,
    description: `Reverted: ${expenseData.description}. Reason: ${reason}`,
  });

  // Mark expense as reverted
  await updateDoc(expenseRef, {
    reverted: true,
    revertReason: reason,
    revertedAt: serverTimestamp(),
    revertedBy: user.uid,
    updatedAt: serverTimestamp(),
  });
};

